/**
 * exportReport · helpers universales de export a PDF y Excel
 * con encabezado/pie institucional (leído de tabla config_reporte).
 *
 * Uso:
 *   const config = await fetchConfigReporte()
 *   await exportarPDF({ config, titulo, subtitulo, headers, rows, totalRow })
 *   await exportarExcel({ config, titulo, subtitulo, headers, rows, totalRow })
 *
 * Task #143.
 */

import type { ConfigReporte } from './useConfigReporte'
import { hoyGT } from './fechas'

export type ExportOpts = {
  config: ConfigReporte | null
  titulo: string
  subtitulo?: string           // ej: "Del 2026-01-01 al 2026-01-31"
  headers: string[]            // encabezados de tabla
  rows: (string | number)[][]  // filas ya formateadas
  totalRow?: (string | number)[]     // fila totales opcional
  filename?: string            // sin extensión
  orientacion?: 'portrait' | 'landscape'
  columnasNumericas?: number[] // índices que se alinean a la derecha en PDF
  // Bloque resumen opcional al final (ej. verificación por clase INGECOP).
  // valor ya formateado como texto; label vacío = separador.
  resumen?: { label: string; valor: string; bold?: boolean }[]
}

const nombreArchivo = (titulo: string) =>
  titulo.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'reporte'

const stamp = () => hoyGT()

// ---------------------------- PDF -----------------------------------------

export async function exportarPDF(opts: ExportOpts) {
  // @ts-ignore -- instalado en runtime, no hay tipos hasta pnpm install
  const { jsPDF } = await import('jspdf')
  // @ts-ignore
  const autoTableMod = await import('jspdf-autotable')
  const autoTable = (autoTableMod as any).default ?? (autoTableMod as any)

  const { config, titulo, subtitulo, headers, rows, totalRow, orientacion = 'portrait', columnasNumericas = [], resumen } = opts
  const doc = new jsPDF({ orientation: orientacion, unit: 'pt', format: 'letter' })
  const w = doc.internal.pageSize.getWidth()

  let y = 40

  // Encabezado institucional
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11)
  doc.text(config?.razon_social ?? 'NEOCOOP, R.L.', w / 2, y, { align: 'center' })
  y += 14

  doc.setFont('helvetica', 'normal'); doc.setFontSize(8)
  if (config?.nit) { doc.text(`NIT ${config.nit}`, w / 2, y, { align: 'center' }); y += 10 }
  if (config?.direccion_fiscal) {
    const dir = doc.splitTextToSize(config.direccion_fiscal, w - 80)
    doc.text(dir, w / 2, y, { align: 'center' })
    y += dir.length * 10
  }

  y += 6
  doc.setDrawColor(200); doc.line(40, y, w - 40, y); y += 14

  // Título del reporte
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13)
  doc.text(titulo, w / 2, y, { align: 'center' }); y += 14
  if (subtitulo) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9)
    doc.text(subtitulo, w / 2, y, { align: 'center' }); y += 14
  }

  // Tabla
  const body = rows.map(r => r.map(String))
  const foot = totalRow ? [totalRow.map(String)] : undefined
  const columnStyles: Record<number, any> = {}
  columnasNumericas.forEach(i => { columnStyles[i] = { halign: 'right' } })

  autoTable(doc, {
    startY: y,
    head: [headers],
    body,
    foot,
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [241, 90, 36], textColor: 255, fontStyle: 'bold' },
    footStyles: { fillColor: [244, 244, 245], textColor: 20, fontStyle: 'bold' },
    columnStyles,
    margin: { left: 40, right: 40 },
    didDrawPage: (_data: any) => {
      // Pie de página en cada hoja
      const pageH = doc.internal.pageSize.getHeight()
      doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(120)
      const linea1 = [
        config?.perito_contador_nombre ? `Perito contador: ${config.perito_contador_nombre}` : null,
        config?.perito_contador_nit ? `NIT ${config.perito_contador_nit}` : null,
      ].filter(Boolean).join(' · ')
      const linea2 = [
        config?.resolucion_igc,
        config?.resolucion_sat,
      ].filter(Boolean).join(' · ')
      if (linea1) doc.text(linea1, 40, pageH - 30)
      if (linea2) {
        const t = doc.splitTextToSize(linea2, w - 80)
        doc.text(t, 40, pageH - 20)
      }
      doc.text(
        `Página ${(doc as any).internal.getNumberOfPages()} · Generado ${stamp()}`,
        w - 40, pageH - 15, { align: 'right' }
      )
      doc.setTextColor(0)
    },
  })

  // Bloque resumen (verificación por clase) alineado a la derecha
  if (resumen && resumen.length) {
    const startY = ((doc as any).lastAutoTable?.finalY ?? y) + 24
    autoTable(doc, {
      startY,
      body: resumen.map(x => [x.label, x.valor]),
      theme: 'plain',
      // Mantener el bloque completo en una sola hoja (no partirlo)
      pageBreak: 'avoid',
      rowPageBreak: 'avoid',
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: {
        0: { halign: 'right', cellWidth: 240 },
        1: { halign: 'right', cellWidth: 120, fontStyle: 'bold' },
      },
      margin: { left: w - 40 - 360, right: 40 },
      didParseCell: (d: any) => {
        if (d.section === 'body' && resumen[d.row.index]?.bold) {
          d.cell.styles.fontStyle = 'bold'
        }
      },
    })
  }

  doc.save(`${opts.filename ?? nombreArchivo(titulo)}-${stamp()}.pdf`)
}

// ---------------------------- Excel ---------------------------------------

export async function exportarExcel(opts: ExportOpts) {
  const XLSX = await import('xlsx')
  const { config, titulo, subtitulo, headers, rows, totalRow, resumen } = opts

  // Estructura tipo INGECOP: 5 filas de encabezado + tabla + totales + pie
  const aoa: (string | number)[][] = []
  aoa.push([config?.razon_social ?? 'NEOCOOP, R.L.'])
  if (config?.nit) aoa.push([`NIT ${config.nit}`])
  if (config?.direccion_fiscal) aoa.push([config.direccion_fiscal])
  aoa.push([''])
  aoa.push([titulo])
  if (subtitulo) aoa.push([subtitulo])
  aoa.push([''])

  const headerRow = aoa.length
  aoa.push(headers)
  rows.forEach(r => aoa.push(r))
  if (totalRow) aoa.push(totalRow)

  // Bloque resumen (verificación por clase) — label en col 0, valor en última col
  if (resumen && resumen.length) {
    const lastCol = Math.max(headers.length - 1, 1)
    aoa.push([''])
    for (const x of resumen) {
      const fila: (string | number)[] = new Array(lastCol + 1).fill('')
      fila[0] = x.label
      fila[lastCol] = x.valor
      aoa.push(fila)
    }
  }

  aoa.push([''])
  if (config?.perito_contador_nombre) {
    aoa.push([`Perito contador: ${config.perito_contador_nombre}${config.perito_contador_nit ? ` · NIT ${config.perito_contador_nit}` : ''}`])
  }
  if (config?.resolucion_igc) aoa.push([config.resolucion_igc])
  if (config?.resolucion_sat) aoa.push([config.resolucion_sat])
  aoa.push([`Generado ${stamp()}`])

  const ws = XLSX.utils.aoa_to_sheet(aoa)

  // Ancho aproximado por columna
  const ncols = Math.max(headers.length, ...rows.map(r => r.length))
  ws['!cols'] = Array.from({ length: ncols }, (_, i) => {
    const maxLen = Math.max(
      headers[i]?.length ?? 0,
      ...rows.map(r => String(r[i] ?? '').length)
    )
    return { wch: Math.min(60, Math.max(12, maxLen + 2)) }
  })

  // Merge del encabezado institucional a lo ancho de la tabla
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: ncols - 1 } },
    { s: { r: headerRow - 2, c: 0 }, e: { r: headerRow - 2, c: ncols - 1 } },
  ]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, titulo.slice(0, 30))
  XLSX.writeFile(wb, `${opts.filename ?? nombreArchivo(titulo)}-${stamp()}.xlsx`)
}
